import React from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquare, FileText, ShieldCheck } from 'lucide-react';
import SEO from '../components/SEO';

/**
 * TREATX-DATA-DELETION-INSTRUCTIONS-01 — public, hash-free, crawler-readable
 * User Data Deletion Instructions page for Meta's
 * "Data deletion instructions URL" (App Settings → Basic).
 *
 * OPERATIONAL INSTRUCTIONS ONLY. It is not a legal agreement, it creates no
 * deletion portal, workflow, database or callback endpoint, and it does not
 * duplicate any legal SSOT: the request itself is made through the EXISTING
 * public Contact page using the EXISTING "Privacy / Data Protection Request"
 * category, and the policies are linked at their canonical Agreement
 * Management URLs.
 */
const CONTACT_HREF = '/#/contact';
const PRIVACY_POLICY_HREF = '/legal/privacy_policy';
const RETENTION_POLICY_HREF = '/legal/personal_data_retention_and_destruction_policy';

const DataDeletionPage = () => {
  const { t } = useTranslation();
  const steps = t('dataDeletion.steps', { returnObjects: true });
  const stepList = Array.isArray(steps) ? steps : [];

  return (
    <div
      className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8"
      data-testid="data-deletion-page"
    >
      <SEO
        title={t('dataDeletion.title', 'User Data Deletion')}
        description={t(
          'dataDeletion.metaDescription',
          'How to request deletion of personal data associated with your use of TreatX.'
        )}
        canonicalPath="/data-deletion"
      />

      <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-sm p-6 sm:p-8 lg:p-10">
        <h1
          className="text-3xl sm:text-4xl font-semibold text-gray-900 tracking-tight"
          data-testid="data-deletion-title"
        >
          {t('dataDeletion.title', 'User Data Deletion')}
        </h1>

        <p className="mt-6 text-base text-gray-700 leading-relaxed" data-testid="data-deletion-intro">
          {t(
            'dataDeletion.intro',
            'You may request the deletion of personal data associated with your use of TreatX, subject to applicable legal obligations and permitted retention periods.'
          )}
        </p>

        <h2 className="mt-10 text-base md:text-lg font-semibold text-gray-900">
          {t('dataDeletion.stepsHeading', 'To submit a deletion request:')}
        </h2>
        <ol
          className="mt-4 space-y-3 list-decimal list-inside text-base text-gray-700"
          data-testid="data-deletion-steps"
        >
          {stepList.map((step, i) => (
            <li key={i} className="leading-relaxed">{step}</li>
          ))}
        </ol>

        <div className="mt-8 rounded-xl border border-gray-200 bg-gray-50 p-4 space-y-3">
          <p className="text-sm text-gray-600 flex items-start gap-2">
            <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" />
            {t(
              'dataDeletion.verification',
              'TreatX may request additional information only where reasonably necessary to verify the requester or process the request.'
            )}
          </p>
          <p className="text-sm text-gray-600 flex items-start gap-2">
            <FileText className="w-4 h-4 mt-0.5 flex-shrink-0 text-gray-400" />
            {t(
              'dataDeletion.retention',
              'Some information may need to be retained where required by applicable law, for the establishment, exercise or defence of legal claims, or for other lawful retention obligations.'
            )}
          </p>
        </div>

        <div className="mt-10">
          <a
            href={CONTACT_HREF}
            className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-medium text-white hover:opacity-90 transition-opacity"
            data-testid="data-deletion-contact-link"
          >
            <MessageSquare className="w-4 h-4" />
            {t('dataDeletion.submitCta', 'Submit a request')}
          </a>
        </div>

        <div className="mt-10 pt-6 border-t border-gray-200 space-y-2 text-sm">
          <div>
            <a
              href={PRIVACY_POLICY_HREF}
              className="text-primary hover:underline"
              data-testid="data-deletion-privacy-policy-link"
            >
              {t('dataDeletion.privacyPolicy', 'Privacy Policy')}
            </a>
          </div>
          <div>
            <a
              href={RETENTION_POLICY_HREF}
              className="text-primary hover:underline"
              data-testid="data-deletion-retention-policy-link"
            >
              {t(
                'dataDeletion.retentionPolicy',
                'Personal Data Retention and Destruction Policy'
              )}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DataDeletionPage;
